<script setup>
import { ref, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  id: {
    type: String,
    default: 'mobile-menu'
  },
  isOpen: {
    type: Boolean,
    default: false
  },
  navItems: {
    type: Array,
    required: true
  },
  activeSection: {
    type: String,
    default: 'home'
  }
})

const emit = defineEmits(['close', 'navigate'])

const menuRef = ref(null)

// Handle escape key press
const handleKeydown = (event) => {
  if (event.key === 'Escape' && props.isOpen) {
    emit('close')
  }
}

// Handle navigation
const handleNavigation = (href) => {
  emit('navigate', href)
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
})
</script>

<template>
  <!-- Mobile Menu -->
  <div
    ref="menuRef"
    class="fixed top-0 right-0 h-full w-80 max-w-[80vw] bg-annapolis-navy shadow-2xl transition-transform duration-300 ease-in-out"
    :style="{
      transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
      zIndex: 9999,
      visibility: isOpen ? 'visible' : 'hidden'
    }"
    role="dialog"
    aria-modal="true"
    aria-labelledby="mobile-menu-title"
  >
    <!-- Menu Header -->
    <div class="flex items-center justify-between p-6 border-b border-gray-700">
      <h2 id="mobile-menu-title" class="text-white text-lg font-semibold">
        Navigation
      </h2>
      <button
        @click="emit('close')"
        class="text-gray-400 hover:text-white p-2 rounded-md hover:bg-gray-700 transition-colors"
        aria-label="Close mobile menu"
      >
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <!-- Navigation Links -->
    <nav class="p-6" role="navigation" aria-label="Mobile navigation">
      <ul class="space-y-4">
        <li v-for="item in navItems" :key="item.id">
          <button
            @click="handleNavigation(item.href)"
            :class="[
              'w-full text-left px-4 py-3 rounded-md transition-colors duration-200 flex items-center justify-between',
              activeSection === item.id
                ? 'text-annapolis-teal bg-annapolis-teal/10 border-l-4 border-annapolis-teal'
                : 'text-gray-300 hover:text-annapolis-teal hover:bg-gray-700/50'
            ]"
            :aria-current="activeSection === item.id ? 'location' : undefined"
          >
            <span class="font-medium">{{ item.label }}</span>
            <svg
              v-if="activeSection === item.id"
              class="w-5 h-5 text-annapolis-teal"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </li>
      </ul>
    </nav>

    <!-- Menu Footer -->
    <div class="absolute bottom-0 left-0 right-0 p-6 border-t border-gray-700">
      <div class="text-center">
        <img
          src="/logo.png"
          alt="Annapolis Labs logo - Professional software solutions company"
          class="w-8 h-8 mx-auto mb-2 rounded-lg"
          aria-hidden="true"
        />
        <p class="text-gray-400 text-sm">Annapolis Labs</p>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Prevent body scroll when menu is open */
:global(body.menu-open) {
  overflow: hidden;
}

/* Fade transition for overlay */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>